// src/components/FileUploadModal.js
// Updated: 10/31/2025 07:18 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { storage, db } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { X, UploadCloud, FileText } from 'lucide-react';

export default function FileUploadModal({ isOpen, onClose }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(''); // <-- The user input state
  const [isPublic, setIsPublic] = useState(false);
  
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const { currentUser, userData } = useAuth();

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setIsPublic(false);
    setUploading(false);
    setProgress(0);
    setError('');
  };

  const uploadFile = useCallback(() => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    // We already check title.trim() in the JSX button disabled prop, but for safety:
    if (title.trim() === '') {
      setError('Document Title is required.');
      return;
    }
    if (!currentUser || !userData?.orgId) {
      setError('You must be logged in to upload files.');
      return;
    }

    setUploading(true);
    setError('');

    const fileId = uuidv4();
    const fileExtension = file.name.split('.').pop();
    const storagePath = `organizations/${userData.orgId}/${fileId}.${fileExtension}`;
    const storageRef = ref(storage, storagePath);

    const metadata = {
      contentType: file.type,
      customMetadata: {
        orgId: userData.orgId,
        uploadedBy: currentUser.uid,
      },
    };

    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        console.error('Upload Error:', error);
        setError('File upload failed. Check storage permissions.');
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        const documentData = {
          orgId: userData.orgId,
          uploaderId: currentUser.uid,
          uploaderName: currentUser.displayName,
          title: title, // <-- THIS IS CORRECT: it reads the *user-modified* state
          fileName: file.name,
          fileType: file.type,
          isPublic: isPublic,
          storagePath: storagePath,
          downloadURL: downloadURL,
          uploadedAt: serverTimestamp(),
        };
        console.log("Firestore Document Data:", documentData); 

        await addDoc(collection(db, 'documents'), documentData);

        resetForm();
        onClose();
      }
    );
  }, [file, title, isPublic, currentUser, userData, onClose]); // Dependencies are now correct

  const onDrop = useCallback((acceptedFiles) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(droppedFile.type)) {
      setError('Invalid file type. Please upload a PDF, JPG, or PNG.');
      return;
    }

    setFile(droppedFile);
    // CRUCIAL: Only pre-fill the title if the user hasn't typed anything yet, or if the input is empty.
    // If we unconditionally set the title here, it overwrites user input.
    if (!title || title.trim() === '') {
        setTitle(droppedFile.name.replace(/\.[^/.]+$/, ""));
    }
    setError('');
  }, [title]); // Added 'title' to dependencies for the conditional logic

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploading,
  });

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <button
          onClick={handleClose}
          disabled={uploading}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="h-6 w-6" />
        </button>
        
        <h2 className="text-xl font-semibold text-gray-900">Upload Document</h2>

        <div className="mt-4 space-y-4">
          
          {/* Document Title Input */}
          <div>
            <label htmlFor="doc-title" className="block text-sm font-medium text-gray-700">
              Document Title
            </label>
            <input
              type="text"
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., 'Q3 Financial Report'"
            />
          </div>

          {/* File Dropzone */}
          <div
            {...getRootProps()}
            className={`
              flex h-48 w-full cursor-pointer flex-col items-center justify-center 
              rounded-lg border-2 border-dashed border-gray-300 
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'bg-gray-50'}
              transition-colors
            `}
          >
            <input {...getInputProps()} />
            
            {file ? (
              <div className="text-center text-green-600">
                <FileText className="mx-auto h-12 w-12" />
                <p className="mt-2 text-sm font-medium">{file.name}</p>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm">
                  Drag & drop a file here, or click to select
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  PDF, JPG, or PNG only
                </p>
              </div>
            )}
          </div>

          {/* Public Checkbox */}
          <div className="flex items-center">
            <input
              id="is-public"
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              disabled={uploading}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is-public" className="ml-2 block text-sm text-gray-700">
              Make viewable by all organization members
            </label>
          </div>

          {uploading && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700">
                Uploading... {Math.round(progress)}%
              </p>
              <div className="mt-1 w-full rounded-full bg-gray-200">
                <div
                  className="rounded-full bg-blue-600 p-0.5 text-center text-xs font-medium leading-none text-blue-100"
                  style={{ width: `${progress}%` }}
                >
                  &nbsp;
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={uploadFile}
              disabled={uploading || !file || !title}
              className="rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
